import { useEffect, useRef } from 'react';

import { Col } from '@opendoor/atoms/box';
import { useAvailableSoonNotifyMe } from 'components/marketplace/helpers/useAvailableSoonNotifyMe';
import { SimilarListings } from 'components/marketplace/property/SimilarListings';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import { isComingSoonProperty } from 'components/properties/helpers/property';
import { Divider } from 'components/shared/Divider';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';
import dynamic from 'next/dynamic';

import { useCertaintyPanelSurfaces } from './CertaintyPanel';
import { PropertyCTAs } from './PropertyCTAs';
import { PropertyDetails } from './PropertyDetails';
import { PropertyStatus } from './PropertyStatus';
import { PropertySummary } from './PropertySummary';
import { RentalScamBanner } from './RentalScamBanner';

const FooterCTA = dynamic(
  () => import('components/marketplace/property/FooterCTA').then((m) => m.FooterCTA),
  {
    ssr: false,
  },
);

type PropertySectionProps = {
  property: PropertyDetailsType;
};

export const PropertySection = ({ property }: PropertySectionProps) => {
  const ctaRef = useRef<HTMLDivElement>(null);
  const { inView } = useInViewAfterLoad({ ref: ctaRef, threshold: 0, initialInView: true });
  const { slotRef, treatment } = useCertaintyPanelSurfaces(property);
  const availableSoonNotifyMe = useAvailableSoonNotifyMe(isComingSoonProperty(property));
  // Exposed = the viewer is on a coming-soon PDP, so the card's CTA slot is in
  // play. Eligibility is arm-independent: the coming-soon state comes from the
  // listing, not the flag, so exposure cannot bias the split. Fires for both
  // arms; QA overrides are excluded by the assignment itself.
  const { isExperimentAssignment, trackExposure } = availableSoonNotifyMe;
  useEffect(() => {
    if (isExperimentAssignment) {
      trackExposure();
    }
  }, [isExperimentAssignment, trackExposure]);

  return (
    <Col className="md:basis-1/2 md:overflow-y-scroll xl:max-w-184">
      <Col className="px-6 pt-6 md:px-12">
        <RentalScamBanner />
        <PropertyStatus property={property} />
        <Col ref={slotRef} data-analytics-name="cosmos-marketplace-pdp-certainty-panel">
          <PropertySummary
            property={property}
            priceCut={treatment?.priceCut ?? null}
            recentlyListed={treatment?.recentlyListed ?? false}
          />
          <Col ref={ctaRef}>
            <PropertyCTAs
              property={property}
              certainty={treatment}
              availableSoonNotifyEnabled={availableSoonNotifyMe.enabled}
            />
          </Col>
        </Col>
        <Divider className="pt-12" />
        <PropertyDetails property={property} />
        <Col as="section">
          <SimilarListings listings={property.similarProperties} />
        </Col>
      </Col>
      <FooterCTA property={property} isVisible={!inView} />
    </Col>
  );
};
